<template>
  <v-card width="375" flat class="account-selection mx-auto">
    <BackButton v-if="!singlePaymentMethod" @click="$emit('abort')"/>

    <div v-if="!registerAccount" class="card-item-container">
      <div class="pageTitle">{{$t('online_wire_transfer')}}</div>
      <div class="pageSubtitle2 mt-7 mb-1">{{$t('select_account')}}</div>
      <v-list flat class="pb-0 mb-5">
          <v-list-item v-for="(alias, index) in cards.aliasList" :key="alias.aliadId"
            class="cardItem"
            :class="{selected: index === selectedAliasIndex}"
            :disabled="isDeleted(alias)"
            @click="selectedAliasIndex = index">
            <v-list-item-content>
              <div v-if="!isDeleted(alias)" class="d-flex align-center">
                <div class="ml-2 card-number">{{ alias.cardMaskedPan }} </div>
                
                <v-spacer/>
                <check-icon class='check' v-if="index === selectedAliasIndex"/>
                <cross-icon class='cross'
                  @click.stop="promptSuppressCard(alias)"/>
              </div> 
              <div v-else class="d-flex align-center">
                <div class="ml-2 deleted-card">{{ $t('deleted_account') }} </div>
              </div>           
            </v-list-item-content>
          </v-list-item>
      </v-list>

      <div class="pageSubtitle button ml-2"
        @click.stop="registerAccount = true">
        {{$t('use_other_account')}}
      </div>
      <check-box
        class="checkbox ml-1 mb-10"
        v-model="termsOfServiceChecked"
        hide-details dense>
        <template v-slot:label>
          <div>
            {{$t("confirm_terms_of_service1")}}
            <a
              :href="termsUrl"
              target="_blank"
              @click.stop=""
            >{{$t('confirm_terms_of_service2')}}</a>
            {{$t('confirm_terms_of_service3')}}
          </div>
        </template>
      </check-box>       
      <div class="service-notice mb-6">{{$t('service_acr')}}</div>
    </div>
    <div v-else class="card-item-container">
      <div>
        <div class="pageTitle">{{$t('online_wire_transfer')}}</div>
        <div class="pageSubtitle2 mt-4 mb-6">{{$t('select_bank_title')}}</div>
        <div>
          <v-list-item
            class="cardItem"
            :class="{selected: !selectIBAN}"
            @click="selectIBAN = false">
            <v-list-item-content>
              <div class="d-flex align-center">
                <img src="@/assets/search-outline.svg"/>
                <div class="ml-4 card-number">{{ $t('select_bank') }} </div>
                <v-spacer/>
                <check-icon class='check' v-if="!selectIBAN"/>
              </div>       
            </v-list-item-content>
          </v-list-item>
        </div>
        <div class="pageSubtitle2 d-flex justify-center my-2">{{$t('or')}}</div>
        <div>
          <v-list-item
            class="cardItem"
            :class="{selected: selectIBAN}"
            @click="selectIBAN = true">
            <v-list-item-content>
              <div class="d-flex align-center">
                <img src="@/assets/bank.svg"/>
                <div class="ml-4 card-number">{{ $t('enter_IBAN') }} </div>
                <v-spacer/>
                <check-icon class='check' v-if="selectIBAN"/>
              </div>       
            </v-list-item-content>
          </v-list-item>
        </div>
        <check-box
          class="checkbox mt-8"
          v-model="registerNewCard"
          :label="$t('save_account_info')">
        </check-box>
        <check-box
          class="checkbox mt-0 mb-10"
          v-model="termsOfServiceChecked"
          hide-details dense>
          <template v-slot:label>
            <div>
              {{$t("confirm_terms_of_service1")}}
              <a
                :href="termsUrl"
                target="_blank"
                @click.stop=""
              >{{$t('confirm_terms_of_service2')}}</a>
              {{$t('confirm_terms_of_service3')}}
            </div>
          </template>
        </check-box>
        <div class="service-notice mb-6">{{$t('service_acr')}}</div>
      </div>
    </div>

    <v-card-actions class="mt-auto pa-0">
      <ConfirmButton
        :disabled="!canSubmit"
        @click="submit"
        class="icon-button">
          <div>{{$t('continue')}}</div>
          <img class="button-icon" src="@/assets/arrow-forward-outline.svg"/>
      </ConfirmButton>
    </v-card-actions>

    <div class="footer">
      <img src="@/assets/lock-closed-outline-dark.svg"/>
      <div>{{$t('Transaction sécurisée par ')}}</div>
      <img src="@/assets/logo_ca_agorapay.png"/>
    </div>

    <v-dialog
      v-model="showDeleteCardPrompt"
      persistent
    >      
      <v-card class="delete-card-prompt">
        <div class="delete-title">{{$t('delete_account')}}</div>
        <div class="delete-card-number">{{cardToDelete.cardMaskedPan}}</div>
        <ConfirmButton
          @click="suppressCard(cardToDelete)">
          {{$t('confirm')}}
        </ConfirmButton>
        <div @click="showDeleteCardPrompt = false" class="abort-button">{{$t('abort')}}</div>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
  import { VCard, VCardActions, VSpacer, VList, VListItem, VListItemContent, VDialog } from 'vuetify/lib'
  import ConfirmButton from '@/components/ConfirmButton'
  import BackButton from '@/components/BackButton'
  import CheckBox from '@/components/CheckBox'
  import RequestHandler from '../../../mixins'
  import CheckIcon from 'vue-material-design-icons/Check.vue'
  import CrossIcon from 'vue-material-design-icons/Close.vue'
  import {mapState} from 'vuex'

  export default {
    name: 'AccountSelectionPISP',
    props: ['cards', 'termsUrl'],
    components: { 
      VCard,
      VCardActions,
      VSpacer,
      BackButton, 
      ConfirmButton,
      CheckBox,
      VList,
      VListItem,
      VListItemContent,
      VDialog,
      CheckIcon,
      CrossIcon
    },
    mixins: [RequestHandler],
    created: function () {
      if (!this.hasAliases) this.registerAccount = true
    },
    computed: {
      ...mapState(['singlePaymentMethod']),
      selectedAlias: function () {
        return this.cards.aliasList ? this.cards.aliasList[this.selectedAliasIndex] : null
      },
      hasAliases: function () {
        return this.cards.aliasList && this.cards.aliasList.length
      },
      canSubmit: function () {
        return this.termsOfServiceChecked && (!this.registerAccount ? this.selectedAliasIndex >= 0 : true)
      }
    },
    methods: {
      submit: function () {
        // console.log(this.registerAccount, this.cards, this.selectIBAN, this.selectedAlias)
        if (this.registerAccount) this.$emit('select', {item: this.cards, registerAlias: this.registerNewCard, startNeedsInput: this.selectIBAN })
        else if (this.selectedAlias) this.$emit('select', {item: this.cards, aliasId: this.selectedAlias.aliasId})
      },
      promptSuppressCard: function (card) {
        this.cardToDelete = card
        this.showDeleteCardPrompt = true
      },
      suppressCard: function (card) {
        this.showDeleteCardPrompt = false
        this.callSuppressAlias(card.aliasId)
        .then(rep => {
          if (!rep) throw new Error(this.$t('error_treatment'))
          const content = rep.content
          if (content && content.statusCode === '0') {
            this.deletedCards.push(card.aliasId)
            if (this.selectedAlias === card) this.selectedAliasIndex = -1
          }
          else if (process.env.NODE_ENV !== 'production') {
            if (this.selectedAlias === card) this.selectedAliasIndex = -1
            this.deletedCards.push(card.aliasId)
          }
        })
      },
      isDeleted: function (card) {
        return this.deletedCards.indexOf(card.aliasId) !== -1
      }
    },
    data: function () {
      return {
        selectedAliasIndex: 0,
        deletedCards: [],
        registerNewCard: false,
        registerAccount: false,
        cardToDelete: {},
        showDeleteCardPrompt: false,
        termsOfServiceChecked: false,
        selectIBAN: false
      }
    }
  }
</script>